import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUser, getStoredMirror, performLogout } from '../../hooks/useAuth';
import { VACANCY_REGISTRY, type VacancyId } from '../../config/vacancies';
import { fallbackMirror } from '../../config/mirrors';
import VacancyCard from './components/VacancyCard';
import { getSessionTheme } from '../../config/themes';
import PortalTopBar from '../../features/portal-frame/PortalTopBar';
import PortalFooter from '../../features/portal-frame/PortalFooter';
import PortalGlows from '../../features/portal-frame/PortalGlows';

export default function VacancySelectionPage() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const mirror = getStoredMirror();

  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleLogout = () => {
    performLogout();
    navigate('/', { replace: true });
  };

  // Неизвестные фронту id отбрасываем; если не осталось ничего — фолбэк.
  let cards = mirror.vacancies
    .map((id) => VACANCY_REGISTRY[id as VacancyId])
    .filter(Boolean);

  if (cards.length === 0) {
    console.warn(`[mirrors] зеркало "${mirror.id}": нет известных вакансий, применён фолбэк`);
    cards = fallbackMirror(mirror.id).vacancies
      .map((id) => VACANCY_REGISTRY[id])
      .filter(Boolean);
  }

  const gridCols = cards.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
  // Одна вакансия в зеркале — выбирать не из чего, экран работает как приветствие.
  const single = cards.length === 1;
  const theme = getSessionTheme();
  const industrial = theme === 'industrial';
  const kicker = single ? 'Личный кабинет' : 'Выбор направления';

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-background-50 flex flex-col relative overflow-hidden theme-grid"
    >
      {!industrial && <PortalGlows />}

      <PortalTopBar industrial={industrial}>
        <div className="flex items-center gap-4">
          <span className="text-sm text-foreground-600">
            {user?.name}
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-logout-box-r-line" />
            </span>
            Выйти
          </button>
        </div>
      </PortalTopBar>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-16 relative z-10">
        <div className="text-center mb-14">
          {industrial ? (
            <p className="eyebrow mb-5">{kicker}</p>
          ) : (
            <span className="inline-block text-xs font-semibold text-primary-500 bg-primary-500/10 border border-primary-500/20 rounded-full px-4 py-1.5 mb-4">
              {kicker}
            </span>
          )}
          <h1 className={`${industrial ? 'text-4xl md:text-5xl mb-4' : 'text-4xl mb-3'} font-bold text-ink`}>
            {single
              ? industrial ? <>Ваш личный <mark>кабинет</mark></> : 'Ваш личный кабинет'
              : industrial ? <>На какой позиции вы <mark>работаете</mark>?</> : 'На какой позиции вы работаете?'}
          </h1>
          <p className="text-foreground-500 text-base">
            {single
              ? 'Расписание, выплаты и связь с менеджером — в одном месте'
              : 'Выберите вашу роль, чтобы перейти в личный кабинет'}
          </p>
        </div>

        <div className={single ? 'grid grid-cols-1 w-full max-w-md' : `grid grid-cols-1 ${gridCols} gap-6 w-full max-w-3xl`}>
          {cards.map((v) => (
            <VacancyCard key={v.id} vacancy={v} onSelect={(route) => navigate(route)} />
          ))}
        </div>
      </main>

      <PortalFooter stripes={industrial} />
    </div>
  );
}
